import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StageBadge, PriorityBadge, StatusBadge } from "@/components/Badges";
import { useLang } from "@/context/LanguageContext";
import {
  DEPARTMENTS,
  STAGES,
  PRIORITIES,
  STATUSES,
  deptLabel,
} from "@/lib/constants";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, Plus, X, ArrowUpDown, Download, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const ALL = "__all__";

export default function ComplaintsListPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState(-1);
  const { lang } = useLang();
  const navigate = useNavigate();

  const filters = {
    department: params.get("department") || "",
    status: params.get("status") || "",
    priority: params.get("priority") || "",
    stage: params.get("stage") || "",
    mobile: params.get("mobile") || "",
    date_from: params.get("date_from") || "",
    date_to: params.get("date_to") || "",
    overdue: params.get("overdue") === "1",
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams(params);
      if (q) p.set("q", q);
      else p.delete("q");
      setParams(p, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => {
    setLoading(true);
    const payload = {
      q: params.get("q") || undefined,
      department: filters.department || undefined,
      status: filters.status || undefined,
      priority: filters.priority || undefined,
      stage: filters.stage || undefined,
      mobile: filters.mobile || undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
      overdue: filters.overdue ? true : undefined,
      sort_by: sortBy,
      sort_dir: sortDir,
      limit: 200,
    };
    api
      .get("/complaints", { params: payload })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, sortBy, sortDir]);

  function setFilter(key, value) {
    const p = new URLSearchParams(params);
    if (value && value !== ALL) p.set(key, value);
    else p.delete(key);
    setParams(p, { replace: true });
  }

  function clearAll() {
    setParams(new URLSearchParams(), { replace: true });
    setQ("");
  }

  function toggleSort(field) {
    if (sortBy === field) setSortDir((d) => -d);
    else {
      setSortBy(field);
      setSortDir(-1);
    }
  }

  const activeFilters = useMemo(
    () => Object.entries(filters).filter(([, v]) => !!v),
    [filters]
  );

  const [exporting, setExporting] = useState(false);
  async function exportCsv() {
    setExporting(true);
    try {
      const res = await api.get("/exports/complaints.csv", {
        params: {
          q: params.get("q") || undefined,
          department: filters.department || undefined,
          status: filters.status || undefined,
          priority: filters.priority || undefined,
          stage: filters.stage || undefined,
          mobile: filters.mobile || undefined,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          sort_by: sortBy,
          sort_dir: sortDir,
        },
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `complaints_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Exported ${data.total} complaint(s) to CSV`);
    } catch (_e) {
      toast.error("CSV export failed");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="complaints-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-2">
            All Complaints · {data.total}
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Complaint Registry
          </h1>
          <p className="text-slate-500 text-sm mt-2">
            Search, filter and manage every registered complaint.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            data-testid="list-export-csv"
            onClick={exportCsv}
            disabled={exporting || data.total === 0}
            className="h-10"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            Export CSV
          </Button>
          <Link
            to="/complaints/new"
            data-testid="list-new-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Complaint
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="list-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by no., name, subject, mobile..."
              className="pl-9 h-10"
            />
          </div>
          <Select
            value={filters.department || ALL}
            onValueChange={(v) => setFilter("department", v)}
          >
            <SelectTrigger data-testid="filter-department" className={`h-10 ${lang === "hi" ? "font-devanagari" : ""}`}>
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ALL}>All departments</SelectItem>
              {DEPARTMENTS.map((d) => (
                <SelectItem key={d} value={d} className={lang === "hi" ? "font-devanagari" : ""}>
                  {deptLabel(d, lang)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.status || ALL}
            onValueChange={(v) => setFilter("status", v)}
          >
            <SelectTrigger data-testid="filter-status" className="h-10">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.priority || ALL}
            onValueChange={(v) => setFilter("priority", v)}
          >
            <SelectTrigger data-testid="filter-priority" className="h-10">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All priorities</SelectItem>
              {PRIORITIES.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.stage || ALL}
            onValueChange={(v) => setFilter("stage", v)}
          >
            <SelectTrigger data-testid="filter-stage" className="h-10">
              <SelectValue placeholder="Stage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All stages</SelectItem>
              {STAGES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(filters.overdue || activeFilters.length > 0) && (
          <div className="flex items-center gap-3 flex-wrap">
            {filters.overdue && (
              <button
                onClick={() => setFilter("overdue", "")}
                data-testid="filter-overdue-chip"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold bg-red-500/10 text-red-600 border border-red-200 hover:bg-red-500/20"
                title="Click to remove"
              >
                Overdue only
                <X className="w-3 h-3" />
              </button>
            )}
            {activeFilters.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                data-testid="filter-clear"
                className="ml-auto text-slate-500 hover:text-slate-900"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Clear filters
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                <SortHeader
                  label="No."
                  field="complaint_no"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <SortHeader
                  label="Applicant / Mobile"
                  field="applicant_name"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <th className="py-3 px-4">Subject</th>
                <th className="py-3 px-4">Department</th>
                <SortHeader
                  label="Stage"
                  field="current_stage"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <th className="py-3 px-4">Status</th>
                <SortHeader
                  label="Priority"
                  field="priority"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <SortHeader
                  label="Submitted"
                  field="created_at"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-slate-500 text-sm">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading &&
                data.items.map((c) => (
                  <tr
                    key={c.id}
                    data-testid={`row-${c.id}`}
                    onClick={() => navigate(`/complaints/${c.id}`)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="py-3 px-4 font-mono-cms text-xs text-slate-900 font-semibold whitespace-nowrap">
                      {c.complaint_no}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm font-semibold text-slate-800">
                        {c.applicant_name}
                      </div>
                      <div className="text-xs text-slate-500 font-mono-cms">
                        {c.mobile}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700 max-w-xs truncate">
                      {c.subject}
                    </td>
                    <td className={`py-3 px-4 text-sm text-foreground/80 ${lang === "hi" ? "font-devanagari" : ""}`}>
                      {deptLabel(c.department, lang)}
                    </td>
                    <td className="py-3 px-4">
                      <StageBadge stage={c.current_stage} />
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="py-3 px-4">
                      <PriorityBadge priority={c.priority} />
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500 whitespace-nowrap">
                      {format(new Date(c.created_at), "dd MMM yyyy")}
                    </td>
                  </tr>
                ))}
              {!loading && data.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    <div className="text-slate-500 text-sm">
                      No complaints match your filters.
                    </div>
                    <Button
                      onClick={clearAll}
                      variant="link"
                      className="text-orange-600 mt-2"
                    >
                      Clear filters
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SortHeader({ label, field, sortBy, sortDir, onClick }) {
  const active = sortBy === field;
  return (
    <th className="py-3 px-4 select-none">
      <button
        type="button"
        onClick={() => onClick(field)}
        data-testid={`sort-${field}`}
        className="inline-flex items-center gap-1 hover:text-slate-900"
      >
        {label}
        <ArrowUpDown
          className={`w-3 h-3 ${active ? "text-orange-600" : "text-slate-400"}`}
          style={active ? { transform: sortDir === 1 ? "rotate(180deg)" : "none" } : {}}
        />
      </button>
    </th>
  );
}
